import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { getCurrentTimestamp } from "../../../../Helper/DatePicker/DateConverter";
import FullScreenDialog from "../../../../Components/FullScreenDialog";
import CloseButton from "../../../../Components/CloseButton";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { getSOHBreakdownDrillDownData, getSOHDetailsTableAction } from "../../../../Api/BatteryHealthApi";
import { encryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import CODE from "../../../../Static/Constants/StatusCodes";
import { usePagesContent, useBaseEntityInfo } from "../../../../Hooks/usePagesContent";
import {
  SOH_BREAKDOWN_COLUMNS_CONFIG,
  SOH_BREAKDOWN_COLUMN_ORDER,
  getSohBreakdownCustomRenderers,
} from "./SOHBreakdownTableConfig";
import { flattenRow, resolveDrillDownSchema, getDrillDownListActionProps, keepIfSameItems, pickDrillDownTableSchema } from "../utils/tableUtils";

// ─── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE    = 20;
const DEFAULT_SORT = [{ column: "sohEst", direction: "ASC" }];

// ─── Component ─────────────────────────────────────────────────────────────────
const DEFAULT_TABLE_META_KEYS = ["SOHDrillDown"];

const SOHBreakdownDrillDownModal = ({
  open,
  onClose,
  selectedDataPoint,
  globalFilters = {},
  // ── override props (used by RUL breakdown or other callers) ──────────────
  getTableData         = null,
  columnsConfig        : columnsConfigProp   = null,
  columnOrderConfig    : columnOrderConfigProp = null,
  customRenderers      : customRenderersProp  = null,
  tableTitle           = undefined,
  emptyMessage         = undefined,
  titlePrefix          = "State of Health",
  // Title shows the display label; the filter always uses sohCategory (wire label).
  getCategoryLabel     = (dataPoint) => dataPoint?.sohCategoryLabel || dataPoint?.sohCategory || "",
  // ── SOH-path extensions (used by the SOH bucket migration sankey) ─────────
  // Same signature as getSOHBreakdownDrillDownData, plus selectedDataPoint last
  // (e.g. to read the clicked month's startDate / endDate).
  fetchDrillDownData   = getSOHBreakdownDrillDownData,
  // (schema) => schema: narrows { columnsConfig, columnOrdering, select, metaInfo }
  // to what fetchDrillDownData's endpoint supports. Keep the reference stable.
  schemaFilter         = null,
  // Extra header line under the title, e.g. "Month: Jul 2026".
  subtitle             = null,
  // Hides CSV download + mail (and row selection) for endpoints with no list-actions.
  hideListActions      = false,
  // false hides the online/offline dot in the vehicle cells (source has no pingStatus).
  showStatusDot        = true,
  // true wraps the vehicle cell text in the shared operationalStatus field indicator
  // (still gated by the org's fieldIndicators.enabled flag). Off for every other caller.
  operationalStatusIndicator = false,
  // Org meta keys under BatteryIntelligence holding this drill-down's `table` config;
  // the first one present wins. Keep the reference stable (module-level constant).
  tableMetaKeys        = DEFAULT_TABLE_META_KEYS,
}) => {
  const pagesContent = usePagesContent();
  const { baseEntityTypePluralLable } = useBaseEntityInfo();
  const resolvedTableTitle = tableTitle ?? `${baseEntityTypePluralLable} List`;
  const resolvedEmptyMessage = emptyMessage ?? `No ${baseEntityTypePluralLable.toLowerCase()} found for the selected SOH category`;

  const onBatteryIDClick = useCallback((record) => {
    const hash = encryptTheParams({ batteryID: record.batteryID, tabValue: "health" });
    window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
  }, []);

  const customRenderers = useMemo(
    () => customRenderersProp
      ?? getSohBreakdownCustomRenderers(onBatteryIDClick, { showStatusDot, operationalStatusIndicator }),
    [customRenderersProp, onBatteryIDClick, showStatusDot, operationalStatusIndicator]
  );

  // ── orgMeta-derived select + metaInfo (used in API call) ──────────────────
  const [orgMetaSelect,   setOrgMetaSelect]   = useState([]);
  const [orgMetaMetaInfo, setOrgMetaMetaInfo] = useState([]);
  const [schemaReady,     setSchemaReady]     = useState(false);

  // ── Table state ───────────────────────────────────────────────────────────
  const [tableData,             setTableData]             = useState([]);
  const [total,                 setTotal]                 = useState(0);
  const [isLoading,             setIsLoading]             = useState(false);
  const [pageNumber,            setPageNumber]            = useState(1);
  const [sortBy,                setSortBy]                = useState(DEFAULT_SORT);
  const [tableColumns,          setTableColumns]          = useState([]);
  const [selectedColumnKeys,    setSelectedColumnKeys]    = useState([]);
  const [initialColumns,        setInitialColumns]        = useState([]);
  const [defaultSelectedKeys,   setDefaultSelectedKeys]   = useState([]);
  const [columnOrder,           setColumnOrder]           = useState([]);
  const [totalSelectedRowKeys,  setTotalSelectedRowKeys]  = useState(new Set());
  const [isSelectAll,           setIsSelectAll]           = useState(false);

  // ── Helper: initialise column state from a config object + order array ────
  const initColumnsFromConfig = useCallback((config, order) => {
    const cols = getColumnsConfig({ tableColumns: config, tableColumnOrder: order, customRenderers });
    setInitialColumns(cols);
    setTableColumns(cols);
    const keys = cols.map((c) => c.dataIndex);
    setSelectedColumnKeys(keys);
    setDefaultSelectedKeys(keys);
    setColumnOrder(cols.map((c) => c.headerLabel));
  }, [customRenderers]);

  // ── Step 1: Initialise column schema from pagesContent (SOHDrillDown) ──────
  useEffect(() => {
    // If columnsConfig is passed as a prop (RUL override), skip pagesContent lookup
    if (columnsConfigProp) {
      initColumnsFromConfig(
        columnsConfigProp,
        columnOrderConfigProp || Object.keys(columnsConfigProp)
      );
      setSchemaReady(true);
      return;
    }

    // Org meta schema when present, else the local static config; then the
    // caller's schemaFilter (if any) narrows it to the supported fields.
    const schema = resolveDrillDownSchema(
      pickDrillDownTableSchema(pagesContent?.metaInfo?.BatteryIntelligence, tableMetaKeys),
      SOH_BREAKDOWN_COLUMNS_CONFIG,
      SOH_BREAKDOWN_COLUMN_ORDER,
      schemaFilter
    );
    // Same items -> keep the current array, so the fetch effect does not rerun when
    // pagesContent changes identity (and the fallback path keeps its initial []).
    setOrgMetaSelect((prev) => keepIfSameItems(prev, schema.select));
    setOrgMetaMetaInfo((prev) => keepIfSameItems(prev, schema.metaInfo));
    initColumnsFromConfig(schema.columnsConfig, schema.columnOrdering);
    setSchemaReady(true);
  }, [pagesContent, columnsConfigProp, columnOrderConfigProp, initColumnsFromConfig, schemaFilter, tableMetaKeys]);

  // ── Reset when modal closes ───────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setTableData([]);
      setPageNumber(1);
      setSortBy(DEFAULT_SORT);
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
    }
  }, [open]);

  // ── Reset pagination when the selected data point changes ─────────────────
  useEffect(() => {
    if (open && selectedDataPoint) {
      setPageNumber(1);
      setSortBy(DEFAULT_SORT);
    }
  }, [open, selectedDataPoint]);

  // ── Step 2+3: Build params from orgMeta → call API → render ──────────────
  useEffect(() => {
    if (!open || !selectedDataPoint || !schemaReady) return;

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        let result;

        if (getTableData) {
          // ── Override path (e.g. RUL breakdown) ────────────────────────────
          result = await getTableData(selectedDataPoint, pageNumber, sortBy);
        } else {
          // ── SOH path: use orgMeta select/metaInfo → real API ───────────────
          const sohBucket = selectedDataPoint?.sohCategory || "";
          // Include the left-side location filter (city/state/depot/mfgYear)
          // if a specific selection was active when the bar was clicked
          const additionalFilters = selectedDataPoint?.locationFilter
            ? [selectedDataPoint.locationFilter]
            : [];
          const res = await fetchDrillDownData(
            globalFilters,
            sohBucket,
            orgMetaSelect,
            orgMetaMetaInfo,
            pageNumber,
            PAGE_SIZE,
            sortBy,
            additionalFilters,
            selectedDataPoint
          );
          if (cancelled) return;

          if (res?.responseStatus?.code === CODE.SUCCESS) {
            const items = res?.response?.data ?? [];
            result = {
              data:  items.map(flattenRow),
              total: res?.response?.paginationInfo?.total ?? res?.response?.total ?? items.length,
            };
          } else {
            result = { data: [], total: 0 };
          }
        }

        if (cancelled) return;
        setTableData(result.data  || []);
        setTotal(result.total || 0);
      } catch (err) {
        console.error("SOHBreakdownDrillDown: fetch error", err);
        if (!cancelled) { setTableData([]); setTotal(0); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [open, selectedDataPoint, pageNumber, sortBy, schemaReady,
      getTableData, globalFilters, orgMetaSelect, orgMetaMetaInfo, fetchDrillDownData]);

  // ── Table interaction handlers ────────────────────────────────────────────
  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  const handleSortChange = (key, order) => {
    setSortBy([{ column: key, direction: order === "asc" ? "ASC" : "DESC" }]);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const getCurrentSorting = () => {
    if (sortBy.length > 0) return { sortBy: sortBy[0].column, orderBy: sortBy[0].direction.toLowerCase() };
    return {};
  };

  const applySelectedColumns = (newKeys) => {
    const filtered = initialColumns.filter((c) => newKeys.includes(c.dataIndex));
    setTableColumns(filtered);
    setSelectedColumnKeys(newKeys);
    setColumnOrder(filtered.map((c) => c.headerLabel));
  };

  const onColumnOrderChange = (newCols) => {
    setTableColumns(newCols);
    setColumnOrder(newCols.map((c) => c.headerLabel));
  };

  const buildDrillDownFilters = useCallback(() => {
    const sohBucket = selectedDataPoint?.sohCategory || "";
    const locationFilters = selectedDataPoint?.locationFilter
      ? [selectedDataPoint.locationFilter]
      : [];
    return [
      ...(sohBucket ? [{ name: "StateOfHealthBucket", value: [sohBucket] }] : []),
      ...locationFilters,
    ];
  }, [selectedDataPoint]);

  const buildActionBody = useCallback((actionType, emailParams = {}) => ({
    actionType,
    select: orgMetaSelect,
    groupBy: ["batteryID"],
    metaInfo: orgMetaMetaInfo,
    filters: buildDrillDownFilters(),
    columns: tableColumns.map((c) => ({ dataIndex: c.dataIndex, headerLabel: c.headerLabel })),
    columnOrder: tableColumns.map((c) => c.dataIndex),
    selectAll: isSelectAll,
    resourceIDs: Array.from(totalSelectedRowKeys),
    sortBy: sortBy[0]?.column || "sohEst",
    orderBy: sortBy[0]?.direction || "ASC",
    fileName: `SOH_DrillDown_BatteryList_${getCurrentTimestamp("YYYY-MM-DD")}.csv`,
    ...emailParams,
  }), [orgMetaSelect, orgMetaMetaInfo, buildDrillDownFilters, tableColumns, isSelectAll, totalSelectedRowKeys, sortBy]);

  const getCSVData = useCallback(async () => {
    // CSV intentionally returns raw backend values (e.g. odometer 78967), not the
    // display-only "k km" scaling used in the table cells, so the export keeps full
    // precision for analysis. The 'distance-km' renderer is presentation-only.
    const res = await getSOHDetailsTableAction(globalFilters, buildActionBody("download"));
    return res?.response?.data ?? [];
  }, [globalFilters, buildActionBody]);

  const onSendEmail = useCallback(async ({ sendTo, sendCc, subject, body }) => {
    await getSOHDetailsTableAction(globalFilters, buildActionBody("mail", {
      emailTo: sendTo,
      emailCc: sendCc,
      subject,
      body,
    }));
  }, [globalFilters, buildActionBody]);

  const getCsvColumnSettings = useCallback(() => ({
    columnOrder: tableColumns.map((c) => c.headerLabel),
  }), [tableColumns]);

  const onSelectChange = (record, selected) => {
    const updated = new Set(totalSelectedRowKeys);
    if (selected) updated.add(record.id);
    else {
      updated.delete(record.id);
      if (isSelectAll) setIsSelectAll(false);
    }
    setTotalSelectedRowKeys(updated);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((r) => r?.id)) : new Set()
    );
  };

  // ── Title parts: e.g. "State of Health" / "Bangalore" / ">98%" ───────────
  // Location can be a long comma-joined list, so it is truncated with an
  // ellipsis (full text on hover) while the prefix and category stay visible.
  const titleParts = useMemo(() => {
    if (!selectedDataPoint) return { loc: "", cat: "" };
    return {
      loc: selectedDataPoint.locationLabel || "All",
      cat: getCategoryLabel(selectedDataPoint),
    };
  }, [selectedDataPoint, getCategoryLabel]);

  return (
    <FullScreenDialog
      open={open}
      handleClose={onClose}
      containerStyles={{ bgcolor: "#FAFAFA", overflowY: "hidden" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "#FAFAFA" }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "1px solid #E6E6E6",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, flex: 1, mr: 2 }}>
            <Typography variant="fw700s20c000000" sx={{ lineHeight: "28px" }}>
              Drill Down:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
              <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px", whiteSpace: "nowrap", flexShrink: 0 }}>
                {titlePrefix} /
              </Typography>
              <Tooltip
                title={titleParts.loc}
                placement="bottom"
                slotProps={{ tooltip: { sx: { fontSize: "13px", lineHeight: 1.5, maxWidth: 520, p: "8px 12px" } } }}
              >
                <Typography
                  variant="fw500s16c8F8F8F"
                  sx={{ lineHeight: "28px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
                >
                  {titleParts.loc}
                </Typography>
              </Tooltip>
              <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px", whiteSpace: "nowrap", flexShrink: 0 }}>
                / {titleParts.cat}
              </Typography>
            </Box>
            {subtitle && (
              <Typography
                variant="fw400s14c686868"
                data-testid="soh-drilldown-subtitle"
                sx={{ lineHeight: "20px" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <CloseButton onClick={onClose} />
        </Box>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2, minHeight: 0 }}>
          <GenericDraggableTableView
            title={resolvedTableTitle}
            showPrimaryButton={false}
            count={total || tableData.length}
            tableColumns={tableColumns}
            selectedColumns={selectedColumnKeys}
            tableData={tableData}
            availableEmailDomains={pagesContent?.metaInfo?.availableEmailDomains || []}
            initialColumns={initialColumns}
            applySelectedColumns={applySelectedColumns}
            defaultSelectedColumns={defaultSelectedKeys}
            isLoading={isLoading}
            disablePagination={false}
            pageNumber={pageNumber}
            rowsPerPage={PAGE_SIZE}
            total={total}
            disableSearch={true}
            filters={{}}
            selectedFilters={{}}
            applyFilters={() => {}}
            disableFiltersSettings={true}
            disableColumnSettings={false}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
            applySort={handleSortChange}
            initialSorting={getCurrentSorting()}
            emptyMessage={resolvedEmptyMessage}
            containerStyles={{ backgroundColor: "#FFFFFF", borderRadius: "8px", border: "1px solid #E6E6E6" }}
            getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
            getTotalRowSelectionCount={() =>
              isSelectAll ? (total || tableData.length) : totalSelectedRowKeys.size
            }
            onClearSelection={() => { setTotalSelectedRowKeys(new Set()); setIsSelectAll(false); }}
            onPageChange={handlePageChange}
            getCsvColumnSettings={getCsvColumnSettings}
            {...getDrillDownListActionProps(hideListActions, {
              getCSVData,
              onSendEmail,
              rowSelection: {
                selectedRowKeys: Array.from(totalSelectedRowKeys),
                onSelect: onSelectChange,
                onSelectAll: onSelectAllChange,
              },
            })}
          />
        </Box>

      </Box>
    </FullScreenDialog>
  );
};

export default SOHBreakdownDrillDownModal;
